import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Clock,
  Users,
  AlertTriangle,
  Trash2,
  Plus,
  Edit2,
  Calendar,
  Check,
  Download,
  CheckCircle,
  TrendingUp,
  Target,
  Tag,
  Shield,
  Info,
  X,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import EditShiftModal from './EditShiftModal';

interface ShiftTemplate {
  id: string;
  startTime: string; // HH:MM format
  endTime: string;   // HH:MM format
  channels: string[];
  agentsNeeded: number;
  daysOfWeek: number[]; // 0-6, Sunday = 0
  status: 'DRAFT' | 'APPROVED';
  shifts: PlannedShift[]; // Individual shift instances
  skillGroupName?: string;
}

interface PlannedShift {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  requiredCount: number;
  assignedCount: number;
  status: 'PLANNED' | 'APPROVED' | 'SCHEDULED' | 'CANCELLED';
  channelAllocations?: ChannelAllocation[];
  skillGroup?: {
    id: string;
    name: string;
  } | null;
}

interface ChannelAllocation {
  id: string;
  channel: 'PHONE' | 'CHAT' | 'EMAIL' | 'SMS' | 'SOCIAL';
  intervalStart: number;
  intervalEnd: number;
  allocatedAgents: number;
  isPrimary: boolean;
}

interface PlanningSummary {
  forecast: {
    peakRequirement: number;
    avgRequirement: number;
    totalVolume: number;
    aht: number;
    shrinkage: number;
  };
  planned: {
    totalAgents: number;
    avgAgentsPerDay: number;
    shiftCount: number;
  };
  coverage: {
    percentage: number;
    status: 'good' | 'understaffed' | 'overstaffed';
  };
  occupancy: {
    predicted: number;
    status: 'optimal' | 'high' | 'low' | 'critical';
  };
  recommendations: Array<{
    timeBlock: string;
    required: number;
    planned: number;
    action: string;
    priority: 'high' | 'medium' | 'low';
  }>;
}

interface SchedulingRule {
  id: string;
  name: string;
  ruleType: string;
  description?: string;
  severity: 'ERROR' | 'WARNING' | 'INFO';
  isActive: boolean;
  conditions?: Record<string, any>;
}

interface RuleViolation {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  severity: 'ERROR' | 'WARNING' | 'INFO';
  message: string;
}

interface GenerationResult {
  shifts: { created: number };
  ruleViolations: {
    total: number;
    errors: number;
    warnings: number;
    details: RuleViolation[];
  };
  gaps: {
    critical: number;
    high: number;
  };
}

interface ShiftPlanningPanelProps {
  forecastId: string;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CHANNEL_COLORS: Record<string, string> = {
  PHONE: 'bg-blue-100 text-blue-700 border-blue-200',
  CHAT: 'bg-green-100 text-green-700 border-green-200',
  EMAIL: 'bg-purple-100 text-purple-700 border-purple-200',
  SMS: 'bg-orange-100 text-orange-700 border-orange-200',
  SOCIAL: 'bg-pink-100 text-pink-700 border-pink-200',
};

export default function ShiftPlanningPanel({ forecastId }: ShiftPlanningPanelProps) {
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const organizationId = useAuthStore((state) => state.user?.organizationId);
  const [isLoading, setIsLoading] = useState(false);
  const [shifts, setShifts] = useState<PlannedShift[]>([]);
  const [planningSummary, setPlanningSummary] = useState<PlanningSummary | null>(null);
  const [schedulingRules, setSchedulingRules] = useState<SchedulingRule[]>([]);
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const [generationResult, setGenerationResult] = useState<GenerationResult | null>(null);

  // Selection state for bulk operations
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());

  // Modal state
  const [editingShift, setEditingShift] = useState<PlannedShift | null>(null);
  const [isCreatingShift, setIsCreatingShift] = useState(false);

  // Load shift planning data
  useEffect(() => {
    if (forecastId) {
      loadShiftPlanningData();
    }
  }, [forecastId]);

  const loadShiftPlanningData = async (manageLoadingState = true) => {
    if (manageLoadingState) setIsLoading(true);
    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      const requests = [
        fetch(`${baseUrl}/forecasts/${forecastId}/shift-planning/shifts?includeAllocations=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${baseUrl}/forecasts/${forecastId}/shift-planning/planning-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ];

      // Only fetch rules if we have an organizationId
      if (organizationId) {
        requests.push(
          fetch(`${baseUrl}/scheduling-rules?organizationId=${organizationId}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
      }

      const [shiftsResponse, summaryResponse, rulesResponse] = await Promise.all(requests);

      if (shiftsResponse.ok) {
        const shiftsData = await shiftsResponse.json();
        setShifts(shiftsData);
      }

      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setPlanningSummary(summaryData);
      }

      if (rulesResponse?.ok) {
        const rulesData = await rulesResponse.json();
        setSchedulingRules(rulesData.filter((r: SchedulingRule) => r.isActive));
      }
    } catch (error) {
      console.error('Failed to load shift planning data:', error);
    } finally {
      if (manageLoadingState) setIsLoading(false);
    }
  };

  // Group shifts into templates by time pattern
  const shiftTemplates = useMemo((): ShiftTemplate[] => {
    if (shifts.length === 0) return [];

    const templates: Map<string, ShiftTemplate> = new Map();

    shifts.forEach((shift) => {
      const startDate = new Date(shift.startTime);
      const endDate = new Date(shift.endTime);

      const startTimeStr = `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`;
      const endTimeStr = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
      const dayOfWeek = startDate.getDay();

      // Collect channels from allocations
      const channels = shift.channelAllocations?.map((a) => a.channel) || [];
      const uniqueChannels = [...new Set(channels)];

      // Include skill group in template key if present
      const skillGroupKey = shift.skillGroup?.id || 'none';
      const templateKey = `${startTimeStr}-${endTimeStr}-${shift.requiredCount}-${skillGroupKey}`;

      if (!templates.has(templateKey)) {
        templates.set(templateKey, {
          id: templateKey,
          startTime: startTimeStr,
          endTime: endTimeStr,
          channels: uniqueChannels,
          agentsNeeded: shift.requiredCount,
          daysOfWeek: [],
          status: shift.status === 'APPROVED' ? 'APPROVED' : 'DRAFT',
          shifts: [],
          skillGroupName: shift.skillGroup?.name,
        });
      }

      const template = templates.get(templateKey)!;
      if (!template.daysOfWeek.includes(dayOfWeek)) {
        template.daysOfWeek.push(dayOfWeek);
      }
      template.shifts.push(shift);

      // Merge channels
      uniqueChannels.forEach((ch) => {
        if (!template.channels.includes(ch)) {
          template.channels.push(ch);
        }
      });
    });

    // Sort templates by start time and sort days within each template
    return Array.from(templates.values())
      .map((t) => ({ ...t, daysOfWeek: t.daysOfWeek.sort((a, b) => a - b) }))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [shifts]);

  const handleCreateNewShift = () => {
    const newShift: PlannedShift = {
      id: 'new-shift',
      name: 'New Shift',
      startTime: new Date().toISOString(),
      endTime: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
      requiredCount: 5,
      assignedCount: 0,
      status: 'PLANNED',
      channelAllocations: [],
    };
    setEditingShift(newShift);
    setIsCreatingShift(true);
  };

  const handleEditTemplate = (template: ShiftTemplate) => {
    // Edit the first shift in the template as representative
    const representativeShift = template.shifts[0];
    if (representativeShift) {
      setEditingShift(representativeShift);
      setIsCreatingShift(false);
    }
  };

  const handleDeleteTemplate = async (template: ShiftTemplate) => {
    const shiftCount = template.shifts.length;
    if (!confirm(`Delete this shift template? This will remove ${shiftCount} shift${shiftCount !== 1 ? 's' : ''}.`)) {
      return;
    }

    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      // Delete all shifts in the template
      await Promise.all(
        template.shifts.map((shift) =>
          fetch(`${baseUrl}/forecasts/${forecastId}/shift-planning/shifts/${shift.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );

      await loadShiftPlanningData();
    } catch (error) {
      console.error('Failed to delete shifts:', error);
      alert('Failed to delete shifts. Please try again.');
    }
  };

  const handleSaveShift = () => {
    loadShiftPlanningData();
    setEditingShift(null);
    setIsCreatingShift(false);
  };

  const handleGenerateSchedule = async () => {
    setIsLoading(true);
    setGenerationResult(null);
    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      const response = await fetch(
        `${baseUrl}/forecasts/${forecastId}/shift-planning/complete-workflow`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            shiftOptions: {
              shiftDuration: 8,
              // Don't specify shiftStartTimes - let backend auto-detect from forecast demand
              minAgentsPerShift: 1,
              enforceRules: true,
            },
            allocationStrategy: 'PRIORITY_BASED',
            createdBy: user?.id || user?.email,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setGenerationResult(result);
        await loadShiftPlanningData(false); // Don't manage loading state - caller handles it
      } else {
        const errorText = await response.text();
        throw new Error(`Failed: ${errorText}`);
      }
    } catch (error) {
      console.error('Failed to generate schedule:', error);
      alert('Failed to generate schedule. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTemplates.size === 0) return;

    const templates = shiftTemplates.filter((t) => selectedTemplates.has(t.id));
    const totalShifts = templates.reduce((sum, t) => sum + t.shifts.length, 0);

    if (!confirm(`Delete ${selectedTemplates.size} shift template${selectedTemplates.size !== 1 ? 's' : ''} (${totalShifts} total shifts)?`)) {
      return;
    }

    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      const allShifts = templates.flatMap((t) => t.shifts);
      await Promise.all(
        allShifts.map((shift) =>
          fetch(`${baseUrl}/forecasts/${forecastId}/shift-planning/shifts/${shift.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );

      setSelectedTemplates(new Set());
      await loadShiftPlanningData();
    } catch (error) {
      console.error('Failed to delete shifts:', error);
      alert('Failed to delete shifts. Please try again.');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedTemplates.size === 0) return;

    const templates = shiftTemplates.filter((t) => selectedTemplates.has(t.id));
    const allShifts = templates.flatMap((t) => t.shifts);

    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      await Promise.all(
        allShifts.map((shift) =>
          fetch(`${baseUrl}/forecasts/${forecastId}/shift-planning/shifts/${shift.id}`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ...shift, status: 'APPROVED' }),
          })
        )
      );

      setSelectedTemplates(new Set());
      await loadShiftPlanningData();
    } catch (error) {
      console.error('Failed to approve shifts:', error);
      alert('Failed to approve shifts. Please try again.');
    }
  };

  const toggleTemplateSelection = (templateId: string) => {
    const newSelection = new Set(selectedTemplates);
    if (newSelection.has(templateId)) {
      newSelection.delete(templateId);
    } else {
      newSelection.add(templateId);
    }
    setSelectedTemplates(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedTemplates.size === shiftTemplates.length) {
      setSelectedTemplates(new Set());
    } else {
      setSelectedTemplates(new Set(shiftTemplates.map((t) => t.id)));
    }
  };

  const formatDaysOfWeek = (days: number[]): string => {
    if (days.length === 7) return 'Every day';
    if (days.length === 5 && !days.includes(0) && !days.includes(6)) return 'Mon-Fri';
    if (days.length === 2 && days.includes(0) && days.includes(6)) return 'Weekends';

    // Check for consecutive days
    const sorted = [...days].sort((a, b) => a - b);
    let isConsecutive = true;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] - sorted[i - 1] !== 1) {
        isConsecutive = false;
        break;
      }
    }

    if (isConsecutive && days.length > 2) {
      return `${DAY_NAMES[sorted[0]]}-${DAY_NAMES[sorted[sorted.length - 1]]}`;
    }

    return days.map((d) => DAY_NAMES[d]).join(', ');
  };

  const formatTimeRange = (start: string, end: string): string => {
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours % 12 || 12;
      return minutes === 0 ? `${displayHour}${period}` : `${displayHour}:${minutes.toString().padStart(2, '0')}${period}`;
    };
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const getDuration = (start: string, end: string): string => {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    let hours = endH - startH;
    let mins = endM - startM;
    if (mins < 0) {
      hours -= 1;
      mins += 60;
    }
    if (hours < 0) hours += 24;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleExportCSV = useCallback(() => {
    if (shifts.length === 0) return;

    const headers = ['Shift Time', 'Duration', 'Agents', 'Channels', 'Days', 'Status'];
    const rows = shiftTemplates.map((template) => [
      formatTimeRange(template.startTime, template.endTime),
      getDuration(template.startTime, template.endTime),
      template.agentsNeeded,
      template.channels.length > 0 ? template.channels.join('; ') : 'All',
      formatDaysOfWeek(template.daysOfWeek),
      template.status,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `shift-plan-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }, [shifts, shiftTemplates]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Shift Planning</h2>
            <p className="text-gray-600 mt-1">
              Review and approve shift templates before generating the schedule
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Rules Info Button */}
            {schedulingRules.length > 0 && (
              <button
                onClick={() => setShowRulesPanel(!showRulesPanel)}
                className={`flex items-center space-x-2 px-3 py-2 border rounded-lg transition-colors ${
                  showRulesPanel
                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                    : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                title="View active business rules"
              >
                <Shield className="h-4 w-4" />
                <span className="text-sm">{schedulingRules.length} Rules</span>
              </button>
            )}
            <button
              onClick={handleCreateNewShift}
              className="flex items-center space-x-2 px-4 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add Shift</span>
            </button>
            <button
              onClick={handleGenerateSchedule}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Calendar className="h-4 w-4" />
              <span>{isLoading ? 'Generating...' : 'Generate Shifts'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Rules Panel */}
      {showRulesPanel && schedulingRules.length > 0 && (
        <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <h3 className="font-medium text-blue-900">Active Business Rules</h3>
            </div>
            <button
              onClick={() => setShowRulesPanel(false)}
              className="p-1 hover:bg-blue-100 rounded"
            >
              <X className="h-4 w-4 text-blue-600" />
            </button>
          </div>
          <div className="grid gap-2">
            {schedulingRules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-start justify-between p-2 bg-white rounded border border-blue-100"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900 text-sm">{rule.name}</span>
                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                      rule.severity === 'ERROR'
                        ? 'bg-red-100 text-red-700'
                        : rule.severity === 'WARNING'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {rule.severity}
                    </span>
                  </div>
                  {rule.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                  )}
                </div>
                <span className="text-xs text-gray-400 ml-2">{rule.ruleType}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generation Result */}
      {generationResult && (
        <div className="mx-6 mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <h3 className="font-medium text-gray-900">Schedule Generated</h3>
            </div>
            <button
              onClick={() => setGenerationResult(null)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-1">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-700">{generationResult.shifts.created}</span>
              <span className="text-gray-600">shifts created</span>
            </div>

            {generationResult.ruleViolations.total > 0 && (
              <>
                {generationResult.ruleViolations.errors > 0 && (
                  <div className="flex items-center space-x-1">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="font-medium text-red-700">{generationResult.ruleViolations.errors}</span>
                    <span className="text-gray-600">shifts skipped (rule violations)</span>
                  </div>
                )}
                {generationResult.ruleViolations.warnings > 0 && (
                  <div className="flex items-center space-x-1">
                    <Info className="h-4 w-4 text-amber-600" />
                    <span className="font-medium text-amber-700">{generationResult.ruleViolations.warnings}</span>
                    <span className="text-gray-600">warnings</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Show violation details if there are any */}
          {generationResult.ruleViolations.details.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-700 mb-2">Rule Violations:</p>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {generationResult.ruleViolations.details.slice(0, 5).map((violation, idx) => (
                  <div
                    key={idx}
                    className={`flex items-start space-x-2 text-xs p-1.5 rounded ${
                      violation.severity === 'ERROR'
                        ? 'bg-red-50 text-red-800'
                        : violation.severity === 'WARNING'
                        ? 'bg-amber-50 text-amber-800'
                        : 'bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                      violation.severity === 'ERROR'
                        ? 'bg-red-200 text-red-800'
                        : violation.severity === 'WARNING'
                        ? 'bg-amber-200 text-amber-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {violation.severity}
                    </span>
                    <span>{violation.message}</span>
                  </div>
                ))}
                {generationResult.ruleViolations.details.length > 5 && (
                  <p className="text-xs text-gray-500 pl-1">
                    ... and {generationResult.ruleViolations.details.length - 5} more
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metrics Cards */}
      {planningSummary && (
        <div className="mx-6 mt-6 grid grid-cols-4 gap-4">
          {/* Planned Agents */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <Users className="h-4 w-4" />
              <span>Planned</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {planningSummary.planned.avgAgentsPerDay}
            </div>
            <div className="text-sm text-gray-500">agents/day avg</div>
          </div>

          {/* Forecast Needed */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <Target className="h-4 w-4" />
              <span>Forecast</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {planningSummary.forecast.peakRequirement}
            </div>
            <div className="text-sm text-gray-500">peak needed</div>
          </div>

          {/* Coverage */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <CheckCircle className="h-4 w-4" />
              <span>Coverage</span>
            </div>
            <div className={`text-2xl font-bold ${
              planningSummary.coverage.status === 'good'
                ? 'text-green-600'
                : planningSummary.coverage.status === 'understaffed'
                ? 'text-amber-600'
                : 'text-blue-600'
            }`}>
              {planningSummary.coverage.percentage}%
            </div>
            <div className="text-sm text-gray-500">
              {planningSummary.coverage.status === 'good' && 'well covered'}
              {planningSummary.coverage.status === 'understaffed' && 'understaffed'}
              {planningSummary.coverage.status === 'overstaffed' && 'overstaffed'}
            </div>
          </div>

          {/* Predicted Occupancy */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <TrendingUp className="h-4 w-4" />
              <span>Occupancy</span>
            </div>
            <div className={`text-2xl font-bold ${
              planningSummary.occupancy.status === 'optimal'
                ? 'text-green-600'
                : planningSummary.occupancy.status === 'high'
                ? 'text-amber-600'
                : planningSummary.occupancy.status === 'critical'
                ? 'text-red-600'
                : 'text-blue-600'
            }`}>
              {planningSummary.occupancy.predicted}%
            </div>
            <div className="text-sm text-gray-500">
              {planningSummary.occupancy.status === 'optimal' && 'optimal (70-85%)'}
              {planningSummary.occupancy.status === 'high' && 'high (85-95%)'}
              {planningSummary.occupancy.status === 'critical' && 'critical (>95%)'}
              {planningSummary.occupancy.status === 'low' && 'low (<60%)'}
            </div>
          </div>
        </div>
      )}

      {/* Coverage Analysis / Recommendations */}
      {planningSummary && (
        <div className="mx-6 mt-4">
          {planningSummary.recommendations.length === 0 ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <h3 className="font-medium text-green-800">Coverage looks good</h3>
                  <p className="text-sm text-green-700 mt-0.5">
                    Planned shifts align well with forecast requirements.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-amber-800">Coverage Analysis</h3>
                  <div className="mt-2 space-y-2">
                    {planningSummary.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <span className={`mr-2 ${
                          rec.priority === 'high' ? 'text-red-600' :
                          rec.priority === 'medium' ? 'text-amber-600' : 'text-gray-500'
                        }`}>
                          {rec.priority === 'high' || rec.priority === 'medium' ? '!' : '-'}
                        </span>
                        <span className="text-amber-800">
                          <span className="font-medium">{rec.timeBlock}:</span>{' '}
                          Need {rec.required} agents, planned {rec.planned}{' '}
                          <span className="text-amber-600 font-medium">
                            {rec.action}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedTemplates.size > 0 && (
        <div className="mx-6 mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
          <span className="text-sm font-medium text-purple-800">
            {selectedTemplates.size} shift{selectedTemplates.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleBulkApprove}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors"
            >
              <Check className="h-4 w-4" />
              <span>Approve</span>
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : shiftTemplates.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Shifts Planned</h3>
            <p className="text-gray-600 mb-6">
              Generate shifts from forecast requirements or create them manually
            </p>
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={handleGenerateSchedule}
                disabled={isLoading}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <Calendar className="h-4 w-4" />
                <span>{isLoading ? 'Generating...' : 'Generate Shifts'}</span>
              </button>
              <button
                onClick={handleCreateNewShift}
                className="inline-flex items-center space-x-2 px-4 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Create Manually</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Row */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTemplates.size === shiftTemplates.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-600">Select all</span>
                </label>
                <span className="text-sm text-gray-500">
                  {shiftTemplates.length} template{shiftTemplates.length !== 1 ? 's' : ''} •{' '}
                  {shiftTemplates.reduce((sum, t) => sum + t.shifts.length, 0)} shift{shiftTemplates.reduce((sum, t) => sum + t.shifts.length, 0) !== 1 ? 's' : ''} total
                </span>
              </div>
              <button
                onClick={handleExportCSV}
                className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
            </div>

            {/* Shift Template Cards */}
            <div className="grid gap-4">
              {shiftTemplates.map((template) => (
                <div
                  key={template.id}
                  className={`border rounded-lg transition-all ${
                    selectedTemplates.has(template.id)
                      ? 'border-purple-300 bg-purple-50/50 ring-1 ring-purple-200'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      {/* Left: Checkbox + Main Info */}
                      <div className="flex items-start space-x-4">
                        <input
                          type="checkbox"
                          checked={selectedTemplates.has(template.id)}
                          onChange={() => toggleTemplateSelection(template.id)}
                          className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />

                        <div className="space-y-2">
                          {/* Time and Duration */}
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center text-gray-900">
                              <Clock className="h-5 w-5 mr-2 text-purple-600" />
                              <span className="text-lg font-semibold">
                                {formatTimeRange(template.startTime, template.endTime)}
                              </span>
                            </div>
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                              {getDuration(template.startTime, template.endTime)}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                              template.status === 'APPROVED'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {template.status}
                            </span>
                          </div>

                          {/* Details Row */}
                          <div className="flex items-center space-x-6 text-sm">
                            {/* Agents */}
                            <div className="flex items-center text-gray-600">
                              <Users className="h-4 w-4 mr-1.5" />
                              <span className="font-medium text-gray-900">{template.agentsNeeded}</span>
                              <span className="ml-1">agents</span>
                            </div>

                            {/* Days */}
                            <div className="flex items-center text-gray-600">
                              <Calendar className="h-4 w-4 mr-1.5" />
                              <span>{formatDaysOfWeek(template.daysOfWeek)}</span>
                              {template.shifts.length > 1 && (
                                <span className="ml-1 text-gray-400">({template.shifts.length} shifts)</span>
                              )}
                            </div>

                            {/* Channels */}
                            <div className="flex items-center space-x-1.5">
                              {template.channels.length > 0 ? (
                                template.channels.map((channel) => (
                                  <span
                                    key={channel}
                                    className={`px-2 py-0.5 text-xs font-medium rounded border ${
                                      CHANNEL_COLORS[channel] || 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {channel}
                                  </span>
                                ))
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                  All Channels
                                </span>
                              )}
                            </div>

                            {/* Team/Skill Group */}
                            {template.skillGroupName && (
                              <div className="flex items-center text-gray-600">
                                <Tag className="h-4 w-4 mr-1.5" />
                                <span className="font-medium text-purple-700">{template.skillGroupName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Edit shift"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete shift"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit/Create Shift Modal */}
      {editingShift && (
        <EditShiftModal
          shift={editingShift}
          forecastId={forecastId}
          isCreating={isCreatingShift}
          onClose={() => {
            setEditingShift(null);
            setIsCreatingShift(false);
          }}
          onSave={handleSaveShift}
        />
      )}
    </div>
  );
}
